import ModalResponsive from '@/components/commons/modal-responsive';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { Input } from '@/components/ui/input';
import { useBlogs } from '@/hooks/api/dashboard/blogs';
import { useDebounceValue } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { ElementType, useBuilderStore } from '@/stores/builder';
import { Check, ExternalLinkIcon, PlusIcon, SearchIcon } from 'lucide-react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';

export interface ModalSelectBlogProps {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  element: ElementType;
}

export default function ModalSelectBlog({
  openModal,
  setOpenModal,
  element,
}: ModalSelectBlogProps) {
  const { storeId } = useParams() as { storeId: string };
  const [keyword, setKeyword] = useState('');

  const q = useDebounceValue(keyword, 500);

  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
      setElementData: state.setElementData,
    }),
    shallow
  );

  const { data: blogs, isLoading: isLoadingBlogs } = useBlogs({ page: 1, pageSize: 100, q });

  return (
    <ModalResponsive
      openModal={openModal}
      title='Select Blog'
      setOpenModal={setOpenModal}
      className='max-w-md'
    >
      <div className='px-6'>
        <Input
          name='keyword'
          type='text'
          size={100}
          placeholder='Search'
          leftContent={<SearchIcon className='mx-auto w-4' />}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>
      <div className='h-[400px] overflow-y-auto pb-4'>
        <Link
          href={`/store/${storeId}/blog/category`}
          target='_blank'
          className={cn(
            'flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-100'
          )}
        >
          <div className='flex items-center gap-x-4 text-primary'>
            <PlusIcon className='h-4 w-4' />
            Create Blogs
          </div>
          <ExternalLinkIcon className='h-4 w-4 text-primary' />
        </Link>
        {isLoadingBlogs ? (
          <div className='flex w-full items-center justify-center py-2'>
            <LoadingAnimationIcon />
          </div>
        ) : (
          blogs?.map((blogs) => {
            const selected = blogs.name === blogs.blog_id;
            return (
              <button
                key={blogs.blog_id}
                onClick={() => {
                  builder.setChildData(element.parentId as string, element.id, {
                    source: blogs.slug,
                  });
                  setOpenModal?.(false);
                }}
                className={cn('group flex w-full items-center justify-between px-6 py-2')}
              >
                <div className='flex items-center gap-x-2'>
                  <span className='truncate text-sm'>{blogs.name}</span>
                </div>
                {selected ? (
                  <Check className='h-4 w-4 text-primary' />
                ) : (
                  <Check className='h-4 w-4 opacity-0 duration-100 group-hover:opacity-100' />
                )}
              </button>
            );
          })
        )}
      </div>
    </ModalResponsive>
  );
}
